import { GitBranch } from 'lucide-react'
import { Tooltip } from '@nextui-org/react'
import React, { useRef } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { Typography } from '~/components/common/Typography'
import { MAX_CONTEXT_UPLOAD_FILES, UploadFileIcon, UploadFolderIcon } from './AttachUploadButton'

/** Match the shared chat-toolbar dropdown gap used by the Ask/Agent mode menu. */
const LANDING_PLUS_MENU_SIDE_OFFSET_PX = 8
/**
 * Icon rail alignment: menu icons sit 12px to the right of the trigger center
 * because of panel padding (`p-2`) plus item start padding (`pl-1`).
 * Shift the anchored menu left so the opened menu feels visually connected.
 */
const LANDING_PLUS_MENU_ALIGN_OFFSET_PX = -12
const MENU_TOOLTIP_OFFSET_PX = 16
const MENU_TOOLTIP_CLASSNAMES = {
  base: 'before:bg-tokens-neutral-bg-action z-40',
  content:
    'max-w-[160px] whitespace-normal break-words rounded-[6px] border-none bg-tokens-neutral-bg-action px-2 py-[6px] font-normal text-[12px] leading-[14px] text-tokens-neutral-text-white select-none'
} as const
const MENU_ITEM_CLASSNAME = 'h-8 w-full justify-start gap-0 pl-1 pr-3'
const SUBMENU_ITEM_DISABLED_CLASSNAME = 'cursor-not-allowed opacity-50'

function LandingChatPlusTriggerIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={16}
      height={16}
      viewBox='0 0 16 16'
      fill='none'
      className='shrink-0 text-tokens-neutral-text-secondary'
      aria-hidden
    >
      <path
        fill='currentColor'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M8.00002 2.3999C8.44185 2.3999 8.80002 2.75807 8.80002 3.1999V7.1999H12.8C13.2419 7.1999 13.6 7.55807 13.6 7.9999C13.6 8.44173 13.2419 8.7999 12.8 8.7999H8.80002V12.7999C8.80002 13.2417 8.44185 13.5999 8.00002 13.5999C7.5582 13.5999 7.20002 13.2417 7.20002 12.7999V8.7999H3.20002C2.7582 8.7999 2.40002 8.44173 2.40002 7.9999C2.40002 7.55807 2.7582 7.1999 3.20002 7.1999L7.20002 7.1999V3.1999C7.20002 2.75807 7.5582 2.3999 8.00002 2.3999Z'
      />
    </svg>
  )
}

function FigmaMenuChevronRightIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={16}
      height={16}
      viewBox='0 0 16 16'
      fill='none'
      className='ml-3 shrink-0 text-tokens-neutral-text-secondary'
      aria-hidden
    >
      <path
        d='M6.1333 3.33325L10.8 7.99992L6.1333 12.6666'
        stroke='currentColor'
        strokeWidth='1.3'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

export interface LandingChatPlusMenuProps {
  onAddRepositories: () => void
  onFilesSelect: (files: FileList) => void
  onFolderSelect: () => void
  disabled?: boolean
  currentUploadCount?: number
}

/**
 * Landing chat: single (+) control for repository and supporting-file uploads.
 */
export function LandingChatPlusMenu({
  onAddRepositories,
  onFilesSelect,
  onFolderSelect,
  disabled = false,
  currentUploadCount = 0
}: LandingChatPlusMenuProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isMaxFilesReached = currentUploadCount >= MAX_CONTEXT_UPLOAD_FILES

  const triggerFilePicker = () => {
    if (!isMaxFilesReached) {
      fileInputRef.current?.click()
    }
  }

  const triggerFolderPicker = () => {
    if (!isMaxFilesReached) {
      onFolderSelect()
    }
  }

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      onFilesSelect(files)
    }
    e.target.value = ''
  }

  const maxFilesTitle = `Maximum ${MAX_CONTEXT_UPLOAD_FILES} files allowed`
  const uploadTooltipContent = (content: string) => (isMaxFilesReached ? maxFilesTitle : content)

  const preventMaxFilesSelect = (event: Event) => {
    if (isMaxFilesReached) {
      event.preventDefault()
      return true
    }
    return false
  }

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            disabled={disabled}
            data-testid='landing-chat-plus-menu-trigger'
            aria-label='Add repositories or files'
            aria-haspopup='menu'
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white transition-colors hover:bg-tokens-neutral-bg-grey-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:bg-tokens-neutral-bg-grey-2'
          >
            <LandingChatPlusTriggerIcon />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='start'
          side='bottom'
          sideOffset={LANDING_PLUS_MENU_SIDE_OFFSET_PX}
          alignOffset={LANDING_PLUS_MENU_ALIGN_OFFSET_PX}
          collisionPadding={12}
          avoidCollisions={false}
          className='w-fit space-y-2 p-2'
        >
          <DropdownMenuItem
            data-testid='landing-chat-add-repositories'
            className={MENU_ITEM_CLASSNAME}
            onSelect={() => onAddRepositories()}
          >
            <Tooltip
              content='Select or upload repo directory.'
              placement='right'
              delay={300}
              offset={MENU_TOOLTIP_OFFSET_PX}
              showArrow
              classNames={MENU_TOOLTIP_CLASSNAMES}
            >
              <span className='flex w-full items-center gap-0'>
                <span className='flex h-8 w-8 shrink-0 items-center justify-center text-tokens-neutral-icon-hover'>
                  <GitBranch className='h-4 w-4 border-0' strokeWidth={1.8} aria-hidden />
                </span>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  className='whitespace-nowrap text-left text-tokens-neutral-text-primary'
                >
                  Add Repositories
                </Typography>
              </span>
            </Tooltip>
          </DropdownMenuItem>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-testid='landing-chat-add-supporting-files'
              className={MENU_ITEM_CLASSNAME}
              rightIcon={<FigmaMenuChevronRightIcon />}
            >
              <span className='flex w-full items-center gap-0'>
                <span className='flex h-8 w-8 shrink-0 items-center justify-center text-tokens-neutral-icon-hover'>
                  <UploadFileIcon />
                </span>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  className='flex w-fit whitespace-nowrap text-left text-tokens-neutral-text-primary'
                >
                  Add Supporting Files
                </Typography>
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className='w-fit space-y-2 p-2'>
              <DropdownMenuItem
                data-testid='landing-chat-add-file'
                aria-disabled={isMaxFilesReached}
                title={isMaxFilesReached ? maxFilesTitle : undefined}
                className={`${MENU_ITEM_CLASSNAME} ${
                  isMaxFilesReached ? SUBMENU_ITEM_DISABLED_CLASSNAME : ''
                }`}
                onSelect={event => {
                  if (preventMaxFilesSelect(event)) return
                  window.setTimeout(() => {
                    triggerFilePicker()
                  }, 0)
                }}
              >
                <Tooltip
                  content={uploadTooltipContent('.docx, .pdf, .png, .jpg, .mp4')}
                  placement='right'
                  delay={300}
                  offset={MENU_TOOLTIP_OFFSET_PX}
                  showArrow
                  classNames={MENU_TOOLTIP_CLASSNAMES}
                >
                  <span className='flex w-full items-center gap-0'>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center text-tokens-neutral-icon-hover'>
                      <UploadFileIcon />
                    </span>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='medium'
                      className='flex w-fit whitespace-nowrap text-left text-tokens-neutral-text-primary'
                    >
                      File
                    </Typography>
                  </span>
                </Tooltip>
              </DropdownMenuItem>

              <DropdownMenuItem
                data-testid='landing-chat-add-folder'
                aria-disabled={isMaxFilesReached}
                title={isMaxFilesReached ? maxFilesTitle : undefined}
                className={`${MENU_ITEM_CLASSNAME} ${
                  isMaxFilesReached ? SUBMENU_ITEM_DISABLED_CLASSNAME : ''
                }`}
                onSelect={event => {
                  if (preventMaxFilesSelect(event)) return
                  window.setTimeout(() => {
                    triggerFolderPicker()
                  }, 0)
                }}
              >
                <Tooltip
                  content={uploadTooltipContent('Upload a supporting folder.')}
                  placement='right'
                  delay={300}
                  offset={MENU_TOOLTIP_OFFSET_PX}
                  showArrow
                  classNames={MENU_TOOLTIP_CLASSNAMES}
                >
                  <span className='flex w-full items-center gap-0'>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center text-tokens-neutral-icon-hover'>
                      <UploadFolderIcon />
                    </span>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='medium'
                      className='flex w-fit whitespace-nowrap text-left text-tokens-neutral-text-primary'
                    >
                      Folder
                    </Typography>
                  </span>
                </Tooltip>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        className='hidden'
        onChange={handleFilesChange}
      />
    </>
  )
}
